import React, { useEffect, useState } from 'react';

import { MagnifyingGlassIcon as SearchIcon } from '@heroicons/react/20/solid';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import Input from '@/components/Input';
import IActivationKey from '@/types/IActivationKey';
import IAnalytics, { IAnalyticsAdmin } from '@/types/IAnalytics';
import { ICardAdmin, ICardUser } from '@/types/ICard';
import IContacts, { IContactAdmin } from '@/types/IContacts';
import { IListPaging } from '@/types/IList';
import IOrder from '@/types/IOrderV2';
import IOrganisation, {
  IOrganisationGroup,
  IPilot,
} from '@/types/IOrganisation';
import { IProfileBase } from '@/types/IProfile';

interface ISearch {
  id: string;
  label?: string;
  description?: string;
  placeholder?: string;
  search: string;
  disabled?: boolean;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  fetchQuery: (search: string) => Promise<{
    data: (
      | IActivationKey
      | IAnalytics
      | IAnalyticsAdmin
      | ICardAdmin
      | ICardUser
      | IContacts
      | IContactAdmin
      | IOrganisationGroup
      | IOrganisation
      | IProfileBase
      | IOrder
      | IPilot
    )[];
    paging: IListPaging;
  }>;
  fetchEnabled?: boolean;
}

export default function Search({
  id,
  label = '',
  description,
  placeholder = 'Search',
  search,
  disabled = false,
  fetchEnabled = true,
  setSearch,
  fetchQuery,
}: ISearch) {
  const [debouncedValue, setDebouncedValue] = useState(search);

  useEffect(() => {
    const handler: NodeJS.Timeout = setTimeout(() => {
      setSearch(debouncedValue);
    }, 500);

    // Cancel the timeout if value changes (also on delay change or unmount)
    return () => {
      clearTimeout(handler);
    };
  }, [debouncedValue, setSearch]);

  useEffect(() => {
    setDebouncedValue(search);
  }, [search]);

  useQuery({
    queryKey: [id],
    queryFn: () => fetchQuery(search),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    staleTime: 0,
    enabled: fetchEnabled,
  });

  return (
    <Input
      id="search"
      label={label}
      description={description}
      placeholder={placeholder}
      type="text"
      autocomplete="off"
      value={debouncedValue}
      onChange={setDebouncedValue}
      icon={SearchIcon}
      disabled={disabled}
    />
  );
}
